import { type Column } from '@tanstack/react-table';
import { ArrowDownIcon, ArrowLeftToLine, ArrowRightToLine, ArrowUpIcon, ChevronsUpDownIcon, EyeOffIcon, PinOff } from 'lucide-react';

import { cn } from '../../utils';
import { Button } from '../button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../dropdown-menu';

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  hide?: boolean;
}

export function DataTableColumnHeader<TData, TValue>({ column, title, className, hide = true }: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return <div className={cn(className)}>{title}</div>;
  }

  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' size='sm' className='-ml-3 h-8 data-[state=open]:bg-accent'>
            <span>{title}</span>
            {column.getIsSorted() === 'desc' ? (
              <ArrowDownIcon className='ml-2 h-4 w-4' />
            ) : column.getIsSorted() === 'asc' ? (
              <ArrowUpIcon className='ml-2 h-4 w-4' />
            ) : (
              <ChevronsUpDownIcon className='ml-2 h-4 w-4' />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start'>
          <DropdownMenuItem
            onClick={() => {
              column.toggleSorting(false);
            }}
          >
            <ArrowUpIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground/70' />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              column.toggleSorting(true);
            }}
          >
            <ArrowDownIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground/70' />
            Desc
          </DropdownMenuItem>
          {!hide && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  column.toggleVisibility(false);
                }}
              >
                <EyeOffIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground/70' />
                Hide
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          {column.getIsPinned() ? (
            <DropdownMenuItem
              onClick={() => {
                column.pin(false);
              }}
              aria-label={`Unpin ${column.columnDef.header as string} column`}
              title={`Unpin ${column.columnDef.header as string} column`}
            >
              <PinOff className='opacity-60 mr-2 size-4' aria-hidden='true' />
              Unpin
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem
                onClick={() => {
                  column.pin('left');
                }}
              >
                <ArrowLeftToLine strokeWidth={2} className='opacity-60 mr-2 size-4' aria-hidden='true' />
                Stick to left
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  column.pin('right');
                }}
              >
                <ArrowRightToLine strokeWidth={2} className='opacity-60 mr-2 size-4' aria-hidden='true' />
                Stick to right
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
